import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import assetsPath from '../../index.js';

/**
 * Nordnet card offer atom. Used in offers molecules.
 *
 * customizedClass - custom class if needed.
 * imageSrc - The img src.
 * title - The text title.
 * subTitle - The text subtitle.
 * isRefurbished - show or not refurbish block. Default value : false.
 * bgType - Background color of the mobile button (ie : primary / secondary). Default value : primary.
 */
@customElement('nn-card-offer')
export class CardOffer extends LitElement {
  static styles = css`
    .white-space {
      white-space: nowrap;
    }
    .figure {
      align-items: center;
      background: var(--nn-card-offer_figure-primary_background-color, #f6f6f6);
      border-radius: var(--nn-card-offer-border-radius, 16px);
      box-sizing: border-box;
      display: inline-flex;
      flex-basis: auto;
      flex-direction: column;
      flex-shrink: 0;
      flex-wrap: wrap;
      font-family: 'font-light';
      font-size: var(--nn-card-offer_figure_font-size, 1em);
      height: var(--nn-card-offer-height, 120px);
      justify-content: flex-start;
      margin: 0;
      max-width: 100%;
      padding: var(--nn-card-offer-padding, 5px);
      text-align: center;
      width: var(--nn-card-offer-width, 123px);
    }

    .figure body {
      margin: 0;
    }
    .figure-primary {
      background: var(--nn-card-offer_figure-primary_background-color, #f6f6f6);
    }
    .figure-secondary {
      background: var(--nn-card-offer_figure-secondary_background-color, #fff);
    }
    .figure * {
      box-sizing: border-box;
    }
    .figure--image {
      align-items: center;
      display: inline-flex;
      flex-basis: auto;
      flex-shrink: 0;
      height: 40px;
      justify-content: center;
      margin-top: 13px;
      width: 100%;
    }
    .figcaption--title {
      color: var(--nn-card-offer_figcaption--title_color, #000);
      flex-basis: auto;
      flex-shrink: 0;
      font-family: 'font-heavy';
      font-size: var(--nn-card-offer_figcaption--title_font-size, 10px);
      line-height: calc(4px + 2ex);
      margin: var(--nn-card-offer_figcaption--title_margin, 0);
      padding: var(--nn-card-offer_figcaption--title_padding, 4px 0 0);
      width: 100%;
    }
    .figcaption--title i {
      font-style: normal;
    }
    .figcaption--text {
      color: var(--nn-card-offer_figcaption--text_color, #000);
      flex-basis: auto;
      flex-shrink: 0;
      font-family: 'font-light';
      font-size: var(--nn-card-offer_figcaption--text_font-size, 10px);
      line-height: calc(4px + 2ex);
      width: 100%;
    }
    .block--refurbish {
      align-items: center;
      background: var(--block-refurbish-color, #5ac834);
      border-radius: var(--nn-card-offer_block-refurbish_border-radius, 32px);
      color: var(--nn-card-offer_block-refurbish_color, #fff);
      display: flex;
      font-size: var(--nn-card-offer_block-refurbish_font-size, 11px);
      gap: var(--nn-card-offer_block-refurbish_gap, 5px);
      height: 20px;
      justify-content: flex-start;
      line-height: 1;
      margin: 0 auto;
      padding: var(--nn-card-offer_block-refurbish_padding, 0 6px);
      width: fit-content;
    }
    .figure.is-refurbished {
      position: relative;
    }
    figure:not(.horizontal) .block--refurbish .img-responsive {
      height: 12px;
      width: 12px;
    }
    figure:not(.horizontal) .block--refurbish {
      position: absolute;
      top: -7px;
      left: 0;
      right: 0;
    }
    .figure.horizontal {
      align-items: center;
      box-sizing: border-box;
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--nn-card-offer_block-figure-horizontal_gap, 8px);
      height: auto;
      min-height: var(
        --nn-card-offer_block-figure-horizontal_mion-height,
        55px
      );
      max-width: 100%;
      padding: var(--figure-horizontal-padding, 4px 24px);
      width: 100%;
    }
    .horizontal .figure--image {
      flex-basis: auto;
      flex-shrink: 0;
      margin: 0;
      width: var(--nn-card-offer_horizontal_figure--image_width, 48px);
    }

    .horizontal figcaption {
      flex-basis: auto;
      flex-shrink: 0;
      margin: 0;
      text-align: left;
      width: calc(100% - 56px);
    }

    .horizontal .figcaption--title {
      font-size: var(
        --nn-card-offer_horizontal-figcaption-title-font-size,
        16px
      );
      line-height: 2ex;
      padding-bottom: var(
        --nn-card-offer_horizontal-figcaption-title-padding-bottom,
        4px
      );
    }

    .horizontal .figcaption--title i {
      font-family: 'font-light';
      font-style: normal;
    }

    .horizontal .figcaption--text {
      font-size: var(
        --nn-card-offer_horizontal-figcaption-text-font-size,
        16px
      );
      line-height: 2ex;
    }
    .horizontal .block--refurbish {
      background: var(
        --nn-card-offer_horizontal_block-refurbish_background,
        #5ac834
      );
      color: var(--nn-card-offer_horizontal_block-refurbish_color, #fff);
      font-size: var(
        --nn-card-offer_horizontal_block-refurbish_font-size,
        calc(16px * 0.75)
      );
      justify-content: flex-start;
      margin: 0;
    }
    @media (max-width: 767px) {
      :host {
        --figure-horizontal-padding: 4px 8px;
      }
    }
  `;

  @property({ type: String }) customizedClass = '';

  @property({ type: String }) imageSrc = '';

  @property({ type: String }) title = '';

  @property({ type: String }) subTitle = '';

  @property({ type: String }) isRefurbished = 'false';

  @property({ type: String }) bgType = 'primary';

  @property({ type: Boolean }) horizontal = false;

  render() {
    return html`
      <figure
        class="figure figure-${this.bgType}${this.horizontal
          ? ' horizontal'
          : ''} ${this.customizedClass}${this.isRefurbished
          ? ' is-refurbished'
          : ''}"
      >
        <div class="figure--image">
          <img
            class="img-responsive"
            src="${assetsPath}/icons/${this.imageSrc}"
            alt=""
            aria-hidden="true"
            loading="lazy"
            width="32"
            height="32"
          />
        </div>
        <figcaption>
          <div class="figcaption--title">${this.parseHtml(this.title)}</div>
          <div class="figcaption--text">
            ${this.subTitle
              ? html` <div class="figure-subtitle">
                  ${this.parseHtml(this.subTitle)}
                </div>`
              : html``}
          </div>
          ${this.isRefurbished === 'true'
            ? html` <div class="block--refurbish">
                <img
                  class="img-responsive"
                  src="${assetsPath}/icons/recycle-white-16.svg"
                  width="16"
                  height="16"
                  alt=""
                />
                reconditionnée
              </div>`
            : html``}
        </figcaption>
      </figure>
    `;
  }

  // eslint-disable-next-line class-methods-use-this
  parseHtml(str?: any) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  }
}
